import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LearnMoreLink } from './learn-more-link';
import { getTimezoneI18nString } from './i18n-mapper';
import { getDisclaimerBannerStyle } from './disclaimer-banner-style';

const commonPropTypes = {
  classes: PropTypes.shape({
    disclaimer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

export const onLinkMRCAccreditedClick = () => window.open('https://about.ads.microsoft.com/en-us/resources/policies/microsoft-advertising-click-measurement-description-of-methodology');

const DisclaimerBannerUnStyledWrapper = ({
  classes,
  i18n,
  instore,
  children, // content for second line
  text,
}) => {
  let finalText = text || i18n.getString(_TL_('Not all metrics are in real time. Clicks and impressions in the last 1 hour, UET conversions in the last 2 hours, and Non-UET conversions (App Install, Offline Conversions) in the last 5 hours might not be included.'));

  if (instore) {
    const instoreText = i18n.getString(_TL_('Conversions from store visits in the last 73 hours might not be included.'));

    finalText = `${finalText} ${instoreText}`;
  }

  return (
    <div className={classNames('disclaimer-banner', classes.disclaimer)}>
      <LearnMoreLink
        linkText={i18n.getString(_TL_('Learn more'))}
        text={finalText}
        topic="ext54480"
      />
      <br />
      {children}
    </div>
  );
};

DisclaimerBannerUnStyledWrapper.propTypes = {
  ...commonPropTypes,
  children: PropTypes.node.isRequired,
  instore: PropTypes.bool.isRequired,
  text: PropTypes.string,
};

DisclaimerBannerUnStyledWrapper.defaultProps = {
  text: '',
};

const DisclaimerBannerUnStyled = ({
  classes,
  i18n,
  timezone,
  instore,
  text,
  children,
}) => {
  let timeZoneStr = typeof timezone === 'string' ? i18n.getString(timezone) : i18n.getString(getTimezoneI18nString(timezone));
  timeZoneStr = timeZoneStr || timezone;
  const mrcAccreditedText = i18n.getString(_TL_('Only search ads clicks are MRC accredited; other metrics are not MRC accredited.'));
  const timeZoneText = `${i18n.getString(_TL_('The time zone for the date range is'))} ${timeZoneStr}`;

  return (
    <DisclaimerBannerUnStyledWrapper
      classes={classes}
      i18n={i18n}
      timezone={timezone}
      instore={instore}
      text={text}
    >
      <LearnMoreLink
        linkText={i18n.getString(_TL_('Learn more. '))}
        text={mrcAccreditedText}
        linkType="delegate"
        onLinkClick={onLinkMRCAccreditedClick}
        topic=""
      />
      <LearnMoreLink
        linkText={i18n.getString(_TL_('Learn more'))}
        text={timeZoneText}
        topic="ext54483"
      />
      {children}
    </DisclaimerBannerUnStyledWrapper>
  );
};

DisclaimerBannerUnStyled.propTypes = {
  ...commonPropTypes,
  timezone: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  instore: PropTypes.bool,
  text: PropTypes.string,
};

DisclaimerBannerUnStyled.defaultProps = {
  instore: false,
  text: '',
};

export const DisclaimerBanner = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerUnStyled),
  getDisclaimerBannerStyle
);

const DisclaimerBannerWithInstoreUnStyled = ({
  classes,
  i18n,
  timezone,
}) => (
  <DisclaimerBannerUnStyled classes={classes} i18n={i18n} timezone={timezone} instore />
);

DisclaimerBannerWithInstoreUnStyled.propTypes = {
  ...commonPropTypes,
  timezone: PropTypes.number.isRequired,
};

export const DisclaimerBannerWithInstore = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerWithInstoreUnStyled),
  getDisclaimerBannerStyle
);

const DisclaimerBannerWithoutTimezoneUnStyled = ({
  classes,
  i18n,
  instore,
  text,
}) => (
  <DisclaimerBannerUnStyledWrapper classes={classes} i18n={i18n} instore={instore} text={text}>
    <LearnMoreLink
      linkText={i18n.getString(_TL_('Learn more'))}
      text={i18n.getString(_TL_('Only search ads clicks are MRC accredited; other metrics are not MRC accredited.'))}
      linkType="delegate"
      onLinkClick={onLinkMRCAccreditedClick}
      topic=""
    />
  </DisclaimerBannerUnStyledWrapper>
);


DisclaimerBannerWithoutTimezoneUnStyled.propTypes = {
  ...commonPropTypes,
  instore: PropTypes.bool,
  text: PropTypes.string,
};

DisclaimerBannerWithoutTimezoneUnStyled.defaultProps = {
  instore: false,
  text: '',
};

export const DisclaimerBannerWithoutTimezone = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerWithoutTimezoneUnStyled),
  getDisclaimerBannerStyle
);

const DisclaimerBannerWithInstoreWithoutTimezoneUnStyled = ({
  classes,
  i18n,
}) => (
  <DisclaimerBannerWithoutTimezoneUnStyled classes={classes} i18n={i18n} instore />
);

DisclaimerBannerWithInstoreWithoutTimezoneUnStyled.propTypes = commonPropTypes;

export const DisclaimerBannerWithInstoreWithoutTimezone = withDefaultStyles(
  withI18nConsumer(DisclaimerBannerWithInstoreWithoutTimezoneUnStyled),
  getDisclaimerBannerStyle
);
